import React, { useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import MarkdownPreview from '~/components/Common/MarkdownPreview';

import { useGetRateGroupRules } from '~/hooks/apis/cruises/useGetRateGroupRules';

import { RateGroup } from '~/services/cruises/RateGroupService';

const FILTER_TYPE_LABELS = {
  RATE_CODE: 'Rate Code',
  RATE_CODE_DESCRIPTION: 'Rate Code Description',
};

interface Props {
  rateGroup: RateGroup;
  vendorId: string | undefined;
}

function RateGroupAccordion({ rateGroup, vendorId }: Props) {
  const [isRulesExpanded, setIsRulesExpanded] = useState(false);
  const rulesQuery = useGetRateGroupRules(vendorId, isRulesExpanded);
  const rateGroupRules = useMemo(() => {
    return rulesQuery.data?.filter((rule) => rule.rateGroupId === rateGroup.id) ?? [];
  }, [rateGroup.id, rulesQuery.data]);

  return (
    <Accordion variant="outlined" sx={{ boxShadow: 1 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`rate-group-${rateGroup.id}-content`}
        id={`rate-group-${rateGroup.id}-header`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontWeight={700}>{rateGroup.name}</Typography>
          <Chip
            label={rateGroup.isRefundable ? 'Refundable' : 'Non-refundable'}
            color={rateGroup.isRefundable ? 'success' : 'default'}
            size="small"
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={1}>
          <Stack spacing={1}>
            <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Description</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MarkdownPreview content={rateGroup.description || ''} />
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Terms and conditions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MarkdownPreview content={rateGroup.termsAndConditions || ''} />
              </AccordionDetails>
            </Accordion>
          </Stack>

          <Accordion
            disableGutters
            elevation={0}
            expanded={isRulesExpanded}
            onChange={(_event, expanded) => setIsRulesExpanded(expanded)}
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`rate-group-${rateGroup.id}-rules-content`}
            >
              <Typography variant="subtitle2">Rules</Typography>
            </AccordionSummary>

            <AccordionDetails>
              {rulesQuery.isLoading && <CircularProgress size={24} aria-label="Loading rate group rules" />}
              {rulesQuery.isError && <Alert severity="error">Unable to load rate group rules.</Alert>}
              {!rulesQuery.isLoading && !rulesQuery.isError && !rateGroupRules.length && (
                <Typography color="text.secondary">No rate group rules created.</Typography>
              )}
              {!rulesQuery.isLoading && !rulesQuery.isError && rateGroupRules.length > 0 && (
                <Stack spacing={2}>
                  {rateGroupRules.map((rule, ruleIndex) => (
                    <Box key={rule.id} sx={{ position: 'relative' }}>
                      <Stack spacing={1} sx={{ border: 1, borderColor: 'divider', p: 1 }}>
                        <Typography fontWeight={700}>Rule {ruleIndex + 1}</Typography>
                        {rule.filters?.map((filter, filterIndex) => (
                          <Box key={filter.id} sx={{ position: 'relative' }}>
                            <Box sx={{ border: 1, borderColor: 'divider', p: 1 }}>
                              <Typography variant="body2">
                                <b>
                                  {FILTER_TYPE_LABELS[filter.filterType]} - {filter.matchType}
                                </b>
                                : {filter.rateCodePattern}
                              </Typography>
                            </Box>
                            {filterIndex < rule.filters.length - 1 && (
                              <Chip
                                label="AND"
                                color="primary"
                                size="small"
                                sx={{ bottom: 0, left: 8, position: 'absolute', transform: 'translateY(50%)' }}
                              />
                            )}
                          </Box>
                        ))}
                      </Stack>
                      {ruleIndex < rateGroupRules.length - 1 && (
                        <Chip
                          label="OR"
                          color="primary"
                          size="small"
                          sx={{ bottom: 0, left: 8, position: 'absolute', transform: 'translateY(50%)' }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default RateGroupAccordion;
